import CoreBase from '../../../services/engines/CoreBase';
import responseHelper from '../../../services/ResponseHelper';
import journalService from '../../../services/JournalService';
import umlPreviewEngine from './UmlPreviewEngine';

/**
 * Saves a given PNG dataUrl as a preview note linked to a schema note (title
 * convention `${schemaRk}.preview`). Pure business logic, no DOM/rendering
 * concerns — the actual clone/capture lives in UmlPreviewDlg (UI layer, see
 * UI/UmlCanvas/UmlPreviewDlg.jsx), same "no React/DOM knowledge in Core"
 * precedent as UmlCanvasEngine.
 *
 * v1: lookup-and-update of an existing preview note is delegated to
 * UmlPreviewEngine/UmlPreviewCache (Cache+Fetcher+Engine pattern) — this class
 * only saves, it doesn't own any preview state itself.
 */
class UmlPreviewUpdater extends CoreBase {
    /**
     * Saves a PNG dataUrl as a preview note linked to the given schema note.
     * @param {{ rk: string, service: string }} note
     * @param {string} dataUrl
     * @returns {Promise<{dataUrl: string, savedNote: object}|null>}
     */
    save = async (note, dataUrl) => {
        if (!note?.rk) {
            this.logError('save', 'no note.rk given, cannot link preview');
            return null;
        }
        const existingRk = umlPreviewEngine.getPreviewRk(note.rk);
        const savedNote = await this.savePreviewNote(note, dataUrl, existingRk);
        if (!savedNote) return null;
        return { dataUrl, savedNote, isUpdate: !!existingRk };
    };

    /**
     * Saves the PNG as a note linked to the schema note by title convention.
     * stateId 0 for now (dev/test visibility in YourData) — TODO: switch to 2
     * (Hidden) once the feature is stable, see Development.Jerny feature note
     * rk=a99f1c87c0134f50847293f246f46344.
     * @param {{ rk: string, service: string }} note
     * @param {string} dataUrl
     * @returns {Promise<object|null>}
     */
    savePreviewNote = async (note, dataUrl, existingRk) => {
        const previewNote = {
            rk: existingRk,
            uid: this.getUid(),
            title: `${note.rk}.preview`,
            service: note.service ?? 'Jerny.UmlWorkspace',
            type: 'UmlSchemaPreview',
            format: 'png-base64',
            remark: '',
            stateId: 0,
            body: dataUrl,
        };

        const resp = await journalService.createJournal({ data: previewNote });
        if (responseHelper.check(resp, '', 'UmlPreviewUpdater.savePreviewNote') < 0) {
            this.logError('savePreviewNote', `code: ${resp?.status}`);
            return null;
        }

        previewNote.rk = resp.data.rk;
        umlPreviewEngine.upsert(previewNote);
        return resp.data;
    };
}

const umlPreviewUpdater = new UmlPreviewUpdater();
export default umlPreviewUpdater;
